/* The characters kept on this device (up to 5): open, share or delete one, or start or import one in a free slot.
   Each card carries a level gem with its class's two domain emblems beside it, and the domain badges;
   CharacterLibrary loads and stores the characters, this only shows them. */

import { useMemo, useState } from 'react';
import { ArrowRight, ClipboardPaste, Plus, Share2, Trash2 } from 'lucide-react';
import { DomainIcon } from './cards/DomainIcon';
import { buildCatalog, classDomains } from './catalog';
import { ImportCodeDialog, ShareCodeDialog } from './share';
import type { ContentVersion } from './content/sources';
import { levelOf } from './leveling';
import type { Character } from './model';

interface Props {
  version: ContentVersion;
  characters: Array<{ id: string; data: Character }>;
  limit: number;
  onOpen: (id: string) => void;
  onNew: () => void;
  onDelete: (id: string) => void;
  /** Adds a character read from a share code. */
  onImport: (c: Character) => void;
}

export default function CharacterRoster({ version, characters, limit, onOpen, onNew, onDelete, onImport }: Props) {
  const catalog = useMemo(() => buildCatalog(version), [version]);
  const [sharing, setSharing] = useState<Character | null>(null);
  const [importing, setImporting] = useState(false);
  const full = characters.length >= limit;
  const empty = Math.max(0, limit - characters.length - 1);
  return (
    <section className="cc-roster" aria-label="Your characters">
      <div className="cc-roster__head">
        <h2 className="cc-roster__title">Your characters</h2>
        <span className="cc-roster__meter" role="img" aria-label={`${characters.length} of ${limit} slots used`}>
          {Array.from({ length: limit }, (_, i) => <span key={i} className={`cc-roster__pip${i < characters.length ? ' is-used' : ''}`} />)}
        </span>
      </div>
      <ul className="cc-roster__list">
        {characters.map(({ id, data: c }) => {
          const name = c.name || 'Unnamed character';
          const domains = classDomains(catalog.cls(c.className));
          const heritage = [c.community, c.ancestry].filter(Boolean).join(' ');
          return (
            <li key={id}>
              <article className={`cc-hero${c.finished ? '' : ' is-draft'}`} aria-label={name}>
                <div className="cc-hero__top">
                  <span className="cc-hero__gem" aria-hidden="true"><span>{c.finished ? levelOf(c) : ''}</span></span>
                  <span className="cc-hero__level">{c.finished ? `Level ${levelOf(c)}` : 'Draft'}</span>
                  <span className="cc-hero__art" aria-hidden="true">
                    {domains.map((d) => <DomainIcon key={d} domain={d} className="cc-hero__emblem" />)}
                  </span>
                </div>
                <h3 className="cc-hero__name">{name}</h3>
                <p className="cc-hero__class">{[c.className, c.subclass].filter(Boolean).join(' · ') || 'No class yet'}</p>
                {heritage && <p className="cc-hero__heritage">{heritage}</p>}
                {domains.length > 0 && (
                  <p className="cc-hero__domains">
                    {domains.map((d) => <span key={d} className={`domain-badge domain-${d.toLowerCase()}`}>{d}</span>)}
                  </p>
                )}
                <div className="cc-hero__actions">
                  <button type="button" className="cb-btn cb-btn--primary cc-hero__open" aria-label={`Open ${name}`} onClick={() => onOpen(id)}>
                    Open<ArrowRight size={15} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="cc-hero__tool"
                    aria-label={`Share code for ${name}`}
                    title="Share code"
                    onClick={() => setSharing(c)}
                  >
                    <Share2 size={15} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="cc-hero__tool"
                    aria-label={`Delete ${name}`}
                    title="Delete"
                    onClick={() => window.confirm(`Delete ${name}? This can't be undone.`) && onDelete(id)}
                  >
                    <Trash2 size={15} aria-hidden="true" />
                  </button>
                </div>
              </article>
            </li>
          );
        })}
        <li className="cc-slot">
          <button type="button" className="cc-slot__new" disabled={full} onClick={onNew}>
            <span className="cc-slot__socket" aria-hidden="true"><Plus size={20} /></span>
            New character
          </button>
          <span className="cc-slot__note">{full ? `All ${limit} slots used` : `Slot ${characters.length + 1} of ${limit}`}</span>
          <button type="button" className="cc-slot__import" disabled={full} onClick={() => setImporting(true)}>
            <ClipboardPaste size={14} aria-hidden="true" />Import a code
          </button>
        </li>
        {Array.from({ length: empty }, (_, i) => (
          <li key={i} className="cc-slot cc-slot--empty" aria-hidden="true">
            <span className="cc-slot__socket" />
          </li>
        ))}
      </ul>
      {full && <p className="cc-note">This device keeps up to {limit} characters. Delete one to make room for another.</p>}
      <p className="cc-note">
        Your characters are saved in this browser only. Clearing your cookies and site data deletes them, so save a PDF of any
        character you want to keep.
      </p>
      {sharing && <ShareCodeDialog character={sharing} onClose={() => setSharing(null)} />}
      {importing && (
        <ImportCodeDialog
          onClose={() => setImporting(false)}
          onImport={(c) => {
            setImporting(false);
            onImport(c);
          }}
        />
      )}
    </section>
  );
}
